import { CalendarCheck, Truck, WashingMachine, Home, Store, CreditCard, Wind, Coffee } from "lucide-react";
import { IMAGES } from "@/lib/config";

const HOME_STEPS = [
  { icon: CalendarCheck, title: "1. Reserva online", desc: "Elige tus servicios, fecha de recogida y paga con tarjeta de forma segura." },
  { icon: Truck, title: "2. Recogemos tu ropa", desc: "Pasamos por tu domicilio en el horario acordado." },
  { icon: WashingMachine, title: "3. Lavamos y cuidamos", desc: "Tratamos cada prenda con productos de primera calidad." },
  { icon: Home, title: "4. Te la devolvemos", desc: "Recibe tu ropa limpia, seca y planchada en 24-48 horas." },
];

const STORE_STEPS = [
  { icon: Store, title: "1. Ven al local", desc: "Abrimos todos los días de 7:30 a 22:30, sin cita previa. Trae tu ropa cuando mejor te venga." },
  { icon: CreditCard, title: "2. Paga en el kiosco", desc: "Elige tu máquina y paga en efectivo, con tarjeta bancaria o ahorra con la Tarjeta VIP Cliente." },
  { icon: Wind, title: "3. Lava y seca", desc: "Lavadoras de 12 y 20 kg y secadoras de 19 kg. En 30-40 minutos tu colada está lista." },
  { icon: Coffee, title: "4. Relájate mientras tanto", desc: "Disfruta de un café o un snack en nuestra zona vending mientras las máquinas trabajan." },
];

const Steps = ({ steps, prefix }) => (
  <div className="space-y-6 sm:space-y-8">
    {steps.map((s) => (
      <div key={s.title} className="flex gap-4 sm:gap-5" data-testid={`${prefix}-step-${s.title[0]}`}>
        <span className="flex-shrink-0 inline-flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-cyan-500 text-white shadow-md">
          <s.icon className="h-5 w-5 sm:h-6 sm:w-6" />
        </span>
        <div>
          <h3 className="font-heading font-semibold text-slate-900">{s.title}</h3>
          <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

export const HowItWorks = () => (
  <section id="como-funciona" className="py-16 sm:py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="max-w-2xl mb-10 sm:mb-14">
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">¿Cómo funciona?</h2>
        <p className="mt-3 text-slate-600">Elige cómo prefieres usar Lavandería Ozono: te lo llevamos a casa o vienes al local.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center" data-testid="how-home-block">
        <div className="md:col-span-5 order-2 md:order-1">
          <img
            src={IMAGES.domicilio}
            alt="Entrega de ropa limpia y doblada en la puerta de casa"
            loading="lazy"
            data-testid="how-home-image"
            className="rounded-2xl shadow-xl w-full object-cover aspect-[16/10] md:aspect-square"
          />
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <span className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-800 text-sm font-medium px-4 py-1.5 rounded-full mb-5 sm:mb-6">
            <Truck className="h-4 w-4" /> Servicio a domicilio
          </span>
          <h3 className="text-xl md:text-2xl font-heading font-bold text-slate-900 mb-6 sm:mb-8">¿Cómo funciona el servicio a domicilio?</h3>
          <Steps steps={HOME_STEPS} prefix="home" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center mt-14 md:mt-20" data-testid="how-store-block">
        <div className="md:col-span-7">
          <span className="inline-flex items-center gap-2 bg-slate-200 text-slate-800 text-sm font-medium px-4 py-1.5 rounded-full mb-5 sm:mb-6">
            <Store className="h-4 w-4" /> Servicio en tienda
          </span>
          <h3 className="text-xl md:text-2xl font-heading font-bold text-slate-900 mb-6 sm:mb-8">¿Cómo funciona el servicio en tienda local?</h3>
          <Steps steps={STORE_STEPS} prefix="store" />
        </div>
        <div className="md:col-span-5">
          <img
            src={IMAGES.laundromat}
            alt="Interior de la lavandería autoservicio"
            loading="lazy"
            className="rounded-2xl shadow-xl w-full object-cover aspect-[16/10] md:aspect-square"
          />
        </div>
      </div>
    </div>
  </section>
);
